import { all, call, fork, put, select, take, takeEvery } from "redux-saga/effects";

import {
  selectKpi,
  selectMainFilter,
  selectSubFilter,
  setGraphLoading,
  setLeftMenuItem,
  setPeriodsItemSelected,
  setSubfilterValues,
  setTableLoading,
  setView,
  STATS_FETCH_DATA,
  STATS_FETCH_SUMMARY_DATA,
  STATS_INIT,
  STATS_PERIOD_CHANGE,
  STATS_PERIOD_CHANGE_FROM_SELECT,
  STATS_SELECT_DIMENSION,
  STATS_SELECT_LEFT_MENU_ITEM,
  STATS_SELECT_LEFT_MENU_KPI,
  STATS_SELECT_LEFT_MENU_SUBITEM,
  STATS_SET_DASHBOARD_DATA,
  STATS_SET_DATA,
  STATS_SET_KPIS,
  STATS_SET_PERIOD,
  STATS_SET_SUMMARY_DATA,
  STATS_SET_TABLE_DATA,
  STATS_SUBFILTER_CHANGE,
  STATS_SUBFILTER_DATA,
  STATS_SUBFILTER_FETCH_DATA,
  STATS_SUBFILTER_LOADING,
  STATS_SET_STATS_FILTERS,
  STATS_SET_ACTIONS,
  STATS_SET_PERIODS
} from "../actions/stats";
import {
  kpisApiUpsellProvider,
  statsApiUpsellProvider,
  actionsApiUpsellProvider,
  commercialApiUsellProvider
} from "../../../lib/api/providers";

import {
  getCurrentDashboardData,
  getCurrentData,
  getCurrentFilters,
  getCurrentFiltersUnSplitted,
  getKpiId,
  getSelectedFilter,
  getSummaryDimensions,
  getSummaryKpiId
} from "../selectors";
import { selectUserKpisIds, selectUserRoleId } from "../../../redux/selectors/user";

import StatUtils from "./../../../utils/stats";
import StatConfigs from "./../../../configs/stats";
import { setMenuLeftIndex, setMenuLeftItemIndex } from "../../../redux/actions/menuActions";
import StatsConfigs from "../../../configs/stats";
import _ from "lodash";
import { currentClientSelector } from "../../../redux/selectors/auth";

export const getStatsState = state => state.stats;
export const getState = state => state;

/**
 * Get filters data
 * @param kpi
 * @param currentFilter
 * @param currentFilters
 * @return {Promise<T | never>}
 */
const getFilterDataApi = (kpi, currentFilter, currentFilters) =>
  statsApiUpsellProvider
    .post(`/filters/${kpi}/${currentFilter}`, currentFilters)
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Get stats data
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
const getPerfData = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`${kpi}`, currentFilters)
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Get table data
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
const getTableData = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`table/${kpi}`, currentFilters)
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Get summary data
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
const getSummaryData = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`summary/${kpi}`, currentFilters)
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Get dashboard data
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
const getDashboardData = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`dashboard/${kpi}`, currentFilters)
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Get Kpis
 * @param body
 * @return {Promise<T | never>}
 */
export const getKpis = body =>
  kpisApiUpsellProvider
    .post(`/`, body)
    .then((response = {}) => {
      return response.data || [];
    })
    .catch(err => {
      throw err;
    });

/**
 * Get Actions
 * @param body
 * @return {Promise<T | never>}
 */
const getActions = body =>
  actionsApiUpsellProvider
    .post(`/`, body)
    .then((response = {}) => {
      return response.data || [];
    })
    .catch(err => {
      throw err;
    });

const getPeriodsApi = (client_id, token) => {
  return commercialApiUsellProvider
    .get(`/${client_id}`, {
      headers: {
        Authorization: token
      }
    })
    .then((response = {}) => {
      return response.data || [];
    })
    .catch(err => {
      throw err;
    });
};
const getStatsFilters = (client_id, token) => {
  return statsApiUpsellProvider
    .get(`/filters/${client_id}`, {
      headers: {
        Authorization: token
      }
    })
    .then((response = {}) => {
      return response.data || [];
    })
    .catch(err => {
      throw err;
    });
};

export function* getFilterData() {
  try {
    yield put({ type: STATS_SUBFILTER_LOADING, loading: true });
    const stats = yield select(getStatsState);
    const subfilterId = stats.subfilterSelected;
    const currentFilters = yield select(getCurrentFilters, subfilterId);
    const formattedFilters = StatUtils.formatFilters(currentFilters);
    formattedFilters.period = stats.period;

    //get kpi to query
    const kpi = /*StatConfigs.kpiToFilter[stats.kpiSelected] ||*/ stats.kpiSelected;

    const data = yield call(getFilterDataApi, kpi, subfilterId, formattedFilters);

    if (!Array.isArray(data)) {
      throw new Error("");
    }

    yield put({
      type: STATS_SUBFILTER_DATA,
      subfilterId: subfilterId,
      data: data
    });
    yield put({ type: STATS_SUBFILTER_LOADING, loading: false });
  } catch (e) {
    yield put({ type: STATS_SUBFILTER_LOADING, loading: false });
  }
}

function* watchLoadUserPage() {
  while (true) {
    const r = yield take("stats/SELECT_MAIN_FILTER");
  }
}

//Fetch stats
function* watchFetchData() {
  yield takeEvery(STATS_FETCH_DATA, fetchData);
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function* refreshPage() {
  yield put(setGraphLoading(true));
  yield call(sleep, 200);
  yield put(setGraphLoading(false));
}

function* fetchData() {
  const statsState = yield select(getStatsState);

  if (!statsState.isDataDisplayed) {
    return;
  }
  try {
    yield put(setGraphLoading(true));
    const stats = yield select(getStatsState);
    switch (stats.view) {
      case "performance": {
        yield call(fetchPerfData);
        break;
      }
      case "table": {
        yield call(fetchTableData);
        break;
      }
      case "dashboard": {
        yield call(fetchDashboardData);
        break;
      }
      case "summary": {
        yield call(fetchSummaryData);
        break;
      }
    }
    yield put(setGraphLoading(false));
  } catch (e) {
    yield put(setGraphLoading(false));
    yield put(setTableLoading(false));
  }
}

function wait(ms) {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * Fetch table data
 * 1/ table data
 * 2/ perf data
 * @return {IterableIterator<*|CallEffect>}
 */
function* fetchTableData() {
  yield put(setTableLoading(true));
  yield call(wait, 500);

  const stats = yield select(getStatsState);
  const kpiSelected = stats.kpiSelected;
  const currentFilters = yield select(getCurrentFiltersUnSplitted);
  const formattedFilters = StatUtils.formatFilters(currentFilters);

  const body = {
    filters: formattedFilters,
    period: stats.period,
    period1: stats.periodToCompare
  };

  const data = yield call(getTableData, kpiSelected, body);

  if (!data) {
    throw new Error();
  }

  yield put({ type: STATS_SET_TABLE_DATA, data: data });

  yield put(setTableLoading(false));
  yield call(fetchPerfData);
}

/**
 * Fetch summary data
 * @return {IterableIterator<*|CallEffect>}
 */
function* fetchSummaryData() {
  const statsState = yield select(getStatsState);

  if (!statsState.isDataDisplayed) {
    return;
  }
  yield put(setTableLoading(true));
  yield call(wait, 500);

  const stats = yield select(getStatsState);
  const kpi = yield select(getKpiId);
  const summaryKpiId = yield select(getSummaryKpiId);
  const summaryDimensions = yield select(getSummaryDimensions);
  const currentFilters = yield select(getCurrentFiltersUnSplitted);
  const formattedFilters = StatUtils.formatFilters(currentFilters);

  const body = {
    filters: formattedFilters,
    period: stats.period,
    period1: stats.periodToCompare,
    summaryKpiId,
    dimensions: summaryDimensions
  };

  const data = yield call(getSummaryData, kpi, body);

  if (!data) {
    throw new Error();
  }

  yield put({ type: STATS_SET_SUMMARY_DATA, data: data });

  yield put(setTableLoading(false));
}

//fetch perf data
function* fetchPerfData() {
  const stats = yield select(getStatsState);
  const kpiSelected = stats.kpiSelected;
  const currentFilters = yield select(getCurrentFilters, null, true);
  const formattedFilters = StatUtils.formatFilters(currentFilters);

  const body = {
    filters: formattedFilters,
    period: stats.period,
    period1: stats.periodToCompare
  };

  const data = yield call(getPerfData, kpiSelected, body);

  if (!data) {
    throw new Error();
  }

  yield put({ type: STATS_SET_DATA, subfilterId: "@one", data: data });
}

//fetch dashboard data
function* fetchDashboardData() {
  const state = yield select(getState);
  const stats = yield select(getStatsState);
  const currentClient = state.currentClient || {};
  const currentClientId = currentClient.id || -1;

  const filters = { id: "dashboard", data: {} };
  filters.data.client_id = [currentClientId];

  const kpiSelected = stats.kpiSelected;

  const body = {
    period: stats.period,
    filters: filters,
    period1: stats.periodToCompare
  };
  const data = yield call(getDashboardData, kpiSelected, body);

  if (!data) {
    throw new Error();
  }

  yield put({ type: STATS_SET_DASHBOARD_DATA, data: data });
}

//Fetch filters data
function* watchFetchFilterData() {
  yield takeEvery(STATS_SUBFILTER_FETCH_DATA, fetchFilterData);
}

function* fetchFilterData({ filter }) {
  yield put(selectSubFilter(filter));
  yield getFilterData();
}

//Filters change
function* watchFilterChanges() {
  yield takeEvery(STATS_SUBFILTER_CHANGE, changeSubfilterValues);
}

function* changeSubfilterValues({ subfilterId, values }) {
  yield put(setSubfilterValues(subfilterId, values));
  yield fetchData();
}

//Period change from select
function* watchPeriodChangesFromSelect() {
  yield takeEvery(STATS_PERIOD_CHANGE_FROM_SELECT, changePeriodFromSelect);
}

function* changePeriodFromSelect(action) {
  yield put(setPeriodsItemSelected([]));
  yield call(changePeriod, action);
}

//Period change
function* watchPeriodChanges() {
  yield takeEvery(STATS_PERIOD_CHANGE, changePeriod);
}

function* changePeriod({ values, periodDesignation }) {
  yield put({ type: STATS_SET_PERIOD, values, periodDesignation });
  yield call(fetchData);
}

//Init page
function* watchInit() {
  yield takeEvery(STATS_INIT, init);
}

function* init({ type, ...queryData }) {
  if (queryData && queryData.filters) {
    return yield call(initWithQuery, { queryData });
  }

  try {
    yield put(setGraphLoading(true));
    const state = yield select(getState);
    const stats = yield select(getStatsState);
    const currentClient = state.currentClient || {};
    const token = state.auth.token || "";
    const currentClientId = currentClient.id || -1;

    //load kpis only once
    if (!stats.kpis || stats.kpis.length === 0) {
      const body = {
        match: {
          client_id: currentClientId
          //format: { $ne: "text" },
          //id: 1,
          //dashboard_display: true
        },
        sort: { priority: 1, id: 1 }
      };
      const userRoleId = selectUserRoleId(state);
      if (userRoleId > 4) {
        body.match.id = {
          $in: selectUserKpisIds(state)
        };
      }
      //retreive kpis
      const kpis = yield call(getKpis, body);
      const actions = yield call(getActions, { match: { client_id: currentClientId }, sort: { priority: 1, id: 1 } });

      if (!kpis) {
        throw new Error();
      }

      //set kpi order
      const orderedKpis = [];
      const displayOrder = StatsConfigs.kpisDisplayOrder || [];
      for (let i = 0; i < displayOrder.length; i++) {
        const displayOrderElement = displayOrder[i];
        const kpi = _.find(kpis, { id: displayOrderElement });
        if (kpi) {
          orderedKpis.push(kpi);
        }
      }

      //add non ordered kpis
      for (let i = 0; i < kpis.length; i++) {
        const kpi = kpis[i];
        const orderedKpiIndex = _.findIndex(orderedKpis, { id: kpi.id });
        if (orderedKpiIndex === -1) {
          orderedKpis.push(kpi);
        }
      }

      yield put({ type: STATS_SET_KPIS, kpis: orderedKpis });
      yield put({ type: STATS_SET_ACTIONS, actions: actions });
    }

    const periods = yield call(getPeriodsApi, currentClientId, token);
    yield put({ type: STATS_SET_PERIODS, periods });
    //select default menu
    //yield call(changeLeftMenuItem, { leftMenuItem: "home", init: true });
    yield call(changeLeftMenuItem, { leftMenuItem: 0, init: true });

    const statsFilters = yield call(getStatsFilters, currentClientId, token);
    yield put({ type: STATS_SET_STATS_FILTERS, statsFilters });

    yield put(setGraphLoading(false));
  } catch (e) {
    console.log(e);
    yield put(setGraphLoading(false));
  }
}

/**
 * Init stats with query from url (permalink)
 * @param queryData
 * @returns {Generator<PutEffect<{type: string, actions: *}>|PutEffect<{type: *}>|PutEffect<{type: *}>|CallEffect|PutEffect<{statsFilters: *, type: string}>|PutEffect<{periods: *, type: string}>|SelectEffect|PutEffect<{type: string, kpis: *[]}>, void, *>}
 */
//TODO refactor
function* initWithQuery({ queryData }) {
  try {
    yield put(setGraphLoading(true));
    const state = yield select(getState);
    let stats = yield select(getStatsState);
    stats = { ...stats, ...queryData };

    const queryFilters = queryData.filters || [];
    const queryClient = queryFilters.client_id || {};
    const queryClientId = (queryClient.values || [])[0] || 0;
    const currentClient = state.currentClient || {};
    const token = state.auth.token || "";

    //get client from query or session if not present in query
    const currentClientId = queryClientId || currentClient.id || -1;
    const orderedKpis = [];
    //load kpis only once
    if (!stats.kpis || stats.kpis.length === 0) {
      const body = {
        match: {
          client_id: currentClientId
        },
        sort: { priority: 1, id: 1 }
      };
      const userRoleId = selectUserRoleId(state);
      if (userRoleId > 4) {
        body.match.id = {
          $in: selectUserKpisIds(state)
        };
      }
      //retreive kpis
      const kpis = yield call(getKpis, body);
      const actions = yield call(getActions, { match: { client_id: currentClientId }, sort: { priority: 1, id: 1 } });

      if (!kpis) {
        throw new Error();
      }

      //set kpi order
      const displayOrder = StatsConfigs.kpisDisplayOrder || [];
      for (let i = 0; i < displayOrder.length; i++) {
        const displayOrderElement = displayOrder[i];
        const kpi = _.find(kpis, { id: displayOrderElement });
        if (kpi) {
          orderedKpis.push(kpi);
        }
      }

      //add non ordered kpis
      for (let i = 0; i < kpis.length; i++) {
        const kpi = kpis[i];
        const orderedKpiIndex = _.findIndex(orderedKpis, { id: kpi.id });
        if (orderedKpiIndex === -1) {
          orderedKpis.push(kpi);
        }
      }

      yield put({ type: STATS_SET_KPIS, kpis: orderedKpis });
      yield put({ type: STATS_SET_ACTIONS, actions: actions });
    }

    const periods = yield call(getPeriodsApi, currentClientId, token);
    yield put({ type: STATS_SET_PERIODS, periods });
    //select default menu
    //refresh stats data from redux
    stats = yield select(getStatsState);
    //set selected kpi from query
    const queryKpiSelected = queryData.kpiSelected || stats.kpiSelected || 0;
    const displayableKpis = stats.kpis.filter(v => v.dashboard_display === true);
    const leftMenuItemIndex = displayableKpis.findIndex(v => v.id === queryKpiSelected);
    const kpiSelected = displayableKpis[leftMenuItemIndex] || {};
    //yield call(changeLeftMenuItem, { leftMenuItem: leftMenuItemIndex || 0, init: true });
    yield put(setLeftMenuItem(leftMenuItemIndex));

    if (kpiSelected) {
      StatConfigs.setDimensions(kpiSelected, currentClientId, stats.actions || []);
      yield put(selectKpi(kpiSelected.id));
      yield put(setMenuLeftIndex(kpiSelected.long_label));

      if (kpiSelected.id === 10 || kpiSelected.id === 26) {
        yield put(selectMainFilter(queryData.mainFilterSelected || "globale"));
        yield call(setViewType, queryData.view || "summary");
      } else {
        yield put(selectMainFilter(queryData.mainFilterSelected || "globale"));
        //yield call(setViewType, "home");
        yield call(setViewType, queryData.view || "dashboard");
      }
    }

    const statsFilters = yield call(getStatsFilters, currentClientId, token);
    yield put({ type: STATS_SET_STATS_FILTERS, statsFilters });

    yield put(setGraphLoading(false));
  } catch (e) {
    console.log(e);
    yield put(setGraphLoading(false));
  }
}

//Left menu item change
function* watchLeftMenuItemChanges() {
  yield takeEvery(STATS_SELECT_LEFT_MENU_ITEM, changeLeftMenuItem);
}

function* changeLeftMenuItem({ leftMenuItem, init = false }) {
  const stats = yield select(getStatsState);
  const currentClient = yield select(currentClientSelector);
  const kpis = stats.kpis || [];
  const actions = stats.actions || [];
  const currentClientId = currentClient.id || -1;
  yield put(setLeftMenuItem(leftMenuItem));
  const displayableKpis = kpis.filter(v => v.dashboard_display === true);
  const kpi = displayableKpis[leftMenuItem] || {};
  if (leftMenuItem === "home") {
    yield put(setMenuLeftIndex("home"));
  } else {
    //const kpi = kpis[leftMenuItem] || {};
    StatConfigs.setDimensions(kpi, currentClientId, actions);
    if (kpi.hasOwnProperty("id")) {
      yield put(selectKpi(kpi.id));
    }

    yield put(setMenuLeftIndex(kpi.long_label));
  }

  //init --> set fist mainFilter
  if (init) {
    if (kpi.id === 10 || kpi.id === 26) {
      yield put(selectMainFilter("globale"));
      yield call(setViewType, "summary");
    } else {
      yield put(selectMainFilter("globale"));
      //yield call(setViewType, "home");
      yield call(setViewType, "dashboard");
    }
  } else {
    //TODO REFACTOR
    //prevent error switch kpi without data / subview
    if (stats.view === "table") {
      yield put(selectMainFilter("globale"));
      //yield call(setViewType, "home");
      yield call(setViewType, "dashboard");
    }

    if (leftMenuItem !== "home" && stats.view === "home") {
      yield call(setViewType, "dashboard");
    } else if (leftMenuItem === "home" && stats.view !== "home") {
      yield call(setViewType, "home");
    } else {
      yield call(loadData);
    }
  }
  return;
}

/**
 * Load data
 * @return {IterableIterator<CallEffect | *>}
 */
function* loadData() {
  const stats = yield select(getStatsState);

  if (!stats.isDataDisplayed) {
    return;
  }
  switch (stats.view) {
    case "table": {
      yield call(fetchTableData);
      break;
    }
    case "performance": {
      yield call(loadPerf);
      break;
    }
    case "dashboard": {
      yield call(loadDashboard);
      break;
    }
    case "summary": {
      yield call(fetchSummaryData);
      break;
    }
  }
}

/**
 * Load performance data
 * @param item
 * @return {IterableIterator<*>}
 */
function* loadPerf() {
  const stats = yield select(getStatsState);

  //if dimension selected, split by dimension
  const currentFilters = yield select(getCurrentFilters);

  if (stats.mainFilterSelected !== "globale" && currentFilters && currentFilters.length === 0) {
    //load default dimension
    yield call(loadDefaultDimension, stats.mainFilterSelected);
    return;
  }
  //fetch data ?
  const currentData = yield select(getCurrentData);

  if (currentData.length === 0) {
    yield call(fetchData);
  }
}

/**
 * load dashboard data
 * @return {IterableIterator<*>}
 */
function* loadDashboard() {
  const currentData = yield select(getCurrentDashboardData);
  if (Object.keys(currentData).length === 0) {
    yield call(fetchData);
  } else {
    yield call(refreshPage);
  }
}

/**
 * Left menu subitem change
 * @return {IterableIterator<ForkEffect | *>}
 */
function* watchLeftMenuSubItemChanges() {
  yield takeEvery(STATS_SELECT_LEFT_MENU_SUBITEM, changeLeftMenuSubItem);
}

function* changeLeftMenuSubItem({ item }) {
  yield call(setViewType, item);
}

function* setViewType(view = "dashboard", load = true) {
  yield put(setView(view));
  yield put(setMenuLeftItemIndex(view));
  if (load) {
    yield call(loadData);
  }
}

/**
 * Dimension change
 * @return {IterableIterator<ForkEffect | *>}
 */
function* watchDimensionChanges() {
  yield takeEvery(STATS_SELECT_DIMENSION, changeDimension);
}

function* changeDimension({ item }) {
  yield put(selectMainFilter(item));
  yield call(loadPerf);
}

/**
 * Add default dimension
 * @param filter
 * @return {IterableIterator<*>}
 */
function* loadDefaultDimension(filter) {
  yield put(selectSubFilter(filter));
  yield call(getFilterData);

  const currentFilter = yield select(getSelectedFilter) || {};
  const currentFilterData = currentFilter.data || [];
  if (currentFilterData.length === 0) {
    return;
  }

  const { id = "" } = currentFilterData[0];
  yield call(changeSubfilterValues, { subfilterId: filter, values: [id] });
}

//Left menu item change
function* watchLeftMenuKpiChange() {
  yield takeEvery(STATS_SELECT_LEFT_MENU_KPI, changeLeftMenuItem2);
}

function* changeLeftMenuItem2({ kpiId, init = false, viewType = "dashboard" }) {
  const state = yield select(getState);
  const stats = yield select(getStatsState);
  const kpis = stats.kpis || [];
  const actions = stats.actions || [];
  const kpi = kpis.find(v => v.id === kpiId);
  const currentClient = state.currentClient || {};
  const currentClientId = currentClient.id || -1;

  StatConfigs.setDimensions(kpi, currentClientId, actions);
  if (kpi.hasOwnProperty("id")) {
    yield put(selectKpi(kpi.id));
  }

  yield put(setMenuLeftIndex(kpi.long_label));

  //init --> set fist mainFilter
  if (init) {
    yield put(selectMainFilter("globale"));
    //yield call(setViewType, "home");
    yield call(setViewType, viewType);
  } else {
    //TODO REFACTOR
    //prevent error switch kpi without data / subview
    if (stats.view === "table") {
      yield put(selectMainFilter("globale"));
      //yield call(setViewType, "home");
      yield call(setViewType, viewType);
    } else {
      yield call(setViewType, viewType, false);
      yield call(loadData);
    }
  }
  return;
}

function* watchFetchSummaryData() {
  yield takeEvery(STATS_FETCH_SUMMARY_DATA, fetchSummaryData);
}

export default function* root() {
  yield all([
    fork(watchFetchFilterData),
    fork(watchFetchData),
    fork(watchInit),
    fork(watchLoadUserPage),
    fork(watchFilterChanges),
    fork(watchPeriodChanges),
    fork(watchPeriodChangesFromSelect),
    fork(watchLeftMenuKpiChange),
    fork(watchLeftMenuItemChanges),
    fork(watchLeftMenuSubItemChanges),
    fork(watchDimensionChanges),
    fork(watchFetchSummaryData)
  ]);
}



// WEBPACK FOOTER //
// src/modules/stats/sagas/index.js